import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, ExternalLink, Plus, CalendarClock, ClipboardCheck, Radio, Gavel, Wand2,
} from "lucide-react";

const PRIORIDAD = {
  critico: { label: "Crítico", cls: "bg-[#fdecea] text-[#7a2514] border border-[#d99584]" },
  importante: { label: "Importante", cls: "bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]" },
  empresarial: { label: "Empresarial", cls: "bg-[#eef1f7] text-[#2b3f63] border border-[#b9c4dc]" },
  informativo: { label: "Informativo", cls: "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]" },
};
const ESTADOS = ["novedad", "pendiente", "implementando", "pruebas", "cumplido", "descartado"];

export default function Cumplimiento() {
  const [tab, setTab] = useState("novedad");
  const [data, setData] = useState({ novedades: [], resumen: {}, monitor: {} });
  const [fuentes, setFuentes] = useState([]);
  const [parametros, setParametros] = useState([]);
  const [auditoria, setAuditoria] = useState(null);
  const [informe, setInforme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [paramOpen, setParamOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, f, p, a, i] = await Promise.all([
        api.get(`/cumplimiento/novedades?estado=${tab === "todos" ? "todos" : tab}`),
        api.get("/cumplimiento/fuentes"),
        api.get("/cumplimiento/parametros"),
        api.get("/cumplimiento/auditoria"),
        api.get("/cumplimiento/informe-semanal"),
      ]);
      setData(n.data); setFuentes(f.data || []); setParametros(p.data || []);
      setAuditoria(a.data); setInforme(i.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const revisar = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.post("/cumplimiento/monitor");
      toast.success(`Monitor ejecutado · ${r.fuentes_revisadas} fuente(s), ${r.novedades_nuevas} novedad(es) nueva(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const inicializarParametros = async () => {
    if (!window.confirm("Se cargarán el IVA vigente, la versión de la ficha técnica y los porcentajes de retención del catálogo actual, con sus fechas de vigencia. ¿Continuar?")) return;
    setLoading(true);
    try {
      const { data: r } = await api.post("/cumplimiento/parametros/inicializar");
      toast.success(`${r.creados} parámetro(s) cargado(s)${r.ya_existian ? ` · ${r.ya_existian} ya existían` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const cambiarEstado = async (n, estado) => {
    try {
      await api.patch(`/cumplimiento/novedades/${n.id}`, { estado });
      toast.success(`${n.titulo.slice(0, 40)}… → ${estado}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const guardarCampo = async (n, campo, valor) => {
    try {
      await api.patch(`/cumplimiento/novedades/${n.id}`, { [campo]: valor });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const conteo = (e) => data.resumen?.[e] ?? 0;
  const paramsVigentes = useMemo(() => parametros.filter(p => p.vigente_hoy), [parametros]);

  return (
    <div className="space-y-6" data-testid="cumplimiento-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Cumplimiento</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <ShieldCheck className="inline mr-2 -mt-1" size={26} />Centro de Cumplimiento Tributario
          </h1>
          <p className="text-sm text-[#4a5c53] mt-1">
            Vigilancia de fuentes oficiales (SRI y Registro Oficial), trazabilidad de cada cambio y
            parámetros tributarios con vigencia. Los cambios fiscales se aplican solo con aprobación manual.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setNuevaOpen(true)} data-testid="cumpl-nueva-btn">
            <Plus size={15} className="mr-2" />Registrar novedad
          </Button>
          <Button onClick={revisar} disabled={loading} className="bg-[#205c45]" data-testid="cumpl-monitor-btn">
            <RefreshCw size={15} className={`mr-2 ${loading ? "animate-spin" : ""}`} />Revisar fuentes ahora
          </Button>
        </div>
      </div>

      {/* Informe semanal + auditoría */}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-[#d1dbd6] bg-white p-3" data-testid="cumpl-informe">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">
            <CalendarClock size={13} className="inline mr-1" />Informe semanal (desde {informe?.desde || "—"})
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            {[["Nuevas", informe?.nuevas_semana], ["Críticas", informe?.criticas_semana],
              ["Afectan al ERP", informe?.que_afectan_erp], ["Afectan a la empresa", informe?.que_afectan_empresa],
              ["Pendientes", informe?.pendientes_totales], ["Fuentes", fuentes.length]].map(([l, v], i) => (
              <div key={i} className="rounded bg-[#f7f9f8] border border-[#d1dbd6] py-1">
                <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">{l}</div>
                <div className="font-bold tabular-nums">{v ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-[#5c7368] mt-2">
            Última revisión automática: {data.monitor?.last_run_at?.slice(0, 16).replace("T", " ") || "pendiente"}
          </div>
        </div>

        <div className="rounded-lg border border-[#d1dbd6] bg-white p-3" data-testid="cumpl-auditoria">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">
            <ClipboardCheck size={13} className="inline mr-1" />Auditoría normativa
            {auditoria && (
              <Badge className={`ml-2 text-[10px] ${auditoria.cumple
                ? "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]"
                : "bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]"}`}>
                {auditoria.cumple ? "Todo en orden" : "Requiere atención"}
              </Badge>
            )}
          </div>
          <ul className="space-y-1 text-xs">
            {(auditoria?.checks || []).map((c, i) => (
              <li key={i} data-testid={`check-${i}`}>
                <div className="flex justify-between gap-2">
                  <span className={c.ok ? "text-[#205c45]" : "text-[#8a5500] font-semibold"}>
                    {c.ok ? "✓" : "!"} {c.item}
                  </span>
                  <span className="font-mono text-[11px] text-[#4a5c53]">{c.valor}</span>
                </div>
                {!c.ok && c.accion && (
                  <div className="text-[11px] text-[#8a5500] mt-0.5">→ {c.accion}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tablero */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#d1dbd6] flex-wrap h-auto">
          {ESTADOS.map(e => (
            <TabsTrigger key={e} value={e} className="capitalize" data-testid={`cumpl-tab-${e}`}>
              {e} ({conteo(e)})
            </TabsTrigger>
          ))}
          <TabsTrigger value="todos" data-testid="cumpl-tab-todos">Todos</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3">
        {(data.novedades || []).length === 0 && !loading && (
          <div className="rounded-lg border-2 border-dashed border-[#d1dbd6] py-10 text-center text-sm text-[#8ba397]"
            data-testid="cumpl-vacio">
            Sin novedades en <strong>{tab}</strong>. Use "Revisar fuentes ahora" para consultar al SRI.
          </div>
        )}
        {(data.novedades || []).map(n => (
          <div key={n.id} className="rounded-lg border border-[#d1dbd6] bg-white p-3 space-y-2"
            data-testid={`novedad-${n.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] ${PRIORIDAD[n.prioridad]?.cls || ""}`}>
                    {PRIORIDAD[n.prioridad]?.label || n.prioridad}
                  </Badge>
                  <Badge className="bg-[#f7f9f8] text-[#4a5c53] border border-[#d1dbd6] text-[10px] capitalize">
                    {n.ambito === "empresa" ? "Afecta a la empresa" : "Afecta al software"}
                  </Badge>
                  {n.resolucion && (
                    <span className="text-[11px] font-mono text-[#205c45]">
                      <Gavel size={11} className="inline mr-1" />{n.resolucion}
                    </span>
                  )}
                </div>
                <div className="font-semibold text-[#15241d] mt-1">{n.titulo}</div>
                <div className="text-[11px] text-[#5c7368]">
                  <Radio size={11} className="inline mr-1" />{n.fuente_nombre}
                  {n.created_at ? ` · detectado ${n.created_at.slice(0, 10)}` : ""}
                  {(n.modulos_afectados || []).length > 0 && ` · módulos: ${n.modulos_afectados.join(", ")}`}
                </div>
                {n.detalle && <p className="text-xs text-[#4a5c53] mt-1 line-clamp-3">{n.detalle}</p>}
                {(n.motivos || []).map((m, i) => (
                  <div key={i} className="text-[11px] text-[#8a5500]">→ {m}</div>
                ))}
              </div>
              {n.url && (
                <a href={n.url} target="_blank" rel="noreferrer"
                  className="text-xs text-[#205c45] underline inline-flex items-center gap-1"
                  data-testid={`fuente-${n.id}`}>
                  Ver fuente <ExternalLink size={12} />
                </a>
              )}
            </div>

            <div className="grid gap-2 md:grid-cols-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Estado</Label>
                <Select value={n.estado} onValueChange={(v) => cambiarEstado(n, v)}>
                  <SelectTrigger className="h-9 text-xs capitalize" data-testid={`estado-${n.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map(e => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Responsable</Label>
                <Input className="h-9 text-xs" defaultValue={n.responsable || ""}
                  onBlur={(e) => e.target.value !== (n.responsable || "") && guardarCampo(n, "responsable", e.target.value)}
                  placeholder="Administración / Desarrollo" data-testid={`responsable-${n.id}`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha límite</Label>
                <Input type="date" className="h-9 text-xs" defaultValue={n.fecha_limite || ""}
                  onChange={(e) => guardarCampo(n, "fecha_limite", e.target.value)}
                  data-testid={`limite-${n.id}`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Archivos / módulos afectados</Label>
                <Input className="h-9 text-xs" defaultValue={n.archivos_afectados || ""}
                  onBlur={(e) => e.target.value !== (n.archivos_afectados || "") && guardarCampo(n, "archivos_afectados", e.target.value)}
                  placeholder="XML factura, retenciones…" data-testid={`archivos-${n.id}`} />
              </div>
            </div>
            <Textarea rows={2} className="text-xs" defaultValue={n.notas || ""}
              onBlur={(e) => e.target.value !== (n.notas || "") && guardarCampo(n, "notas", e.target.value)}
              placeholder="Notas de la revisión contable/técnica…" data-testid={`notas-${n.id}`} />
          </div>
        ))}
      </div>

      {/* Motor tributario */}
      <div className="rounded-lg border border-[#d1dbd6] bg-white overflow-hidden" data-testid="cumpl-parametros">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#d1dbd6]">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
              Motor tributario · parámetros con vigencia
            </div>
            <div className="text-[11px] text-[#5c7368]">
              {paramsVigentes.length} vigente(s) hoy · el histórico se conserva para interpretar documentos antiguos
            </div>
          </div>
          <div className="flex gap-2">
            {parametros.length === 0 && (
              <Button size="sm" onClick={inicializarParametros} disabled={loading}
                className="bg-[#205c45]" data-testid="param-inicializar-btn">
                <Wand2 size={14} className="mr-1" />Cargar parámetros base
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setParamOpen(true)} data-testid="param-nuevo-btn">
              <Plus size={14} className="mr-1" />Nuevo parámetro
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Clave</th><th>Descripción</th><th className="text-right">Valor</th>
                <th>Vigente desde</th><th>Hasta</th><th>Fuente</th><th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {parametros.map(p => (
                <tr key={p.id} data-testid={`param-${p.id}`}>
                  <td className="font-mono text-xs">{p.clave}</td>
                  <td className="text-xs">{p.descripcion}</td>
                  <td className="text-right text-xs font-semibold tabular-nums">
                    {p.valor}{p.tipo === "porcentaje" ? " %" : ""}
                  </td>
                  <td className="text-xs">{p.vigente_desde}</td>
                  <td className="text-xs">{p.vigente_hasta || "—"}</td>
                  <td className="text-xs">{p.fuente || "—"}</td>
                  <td>
                    <Badge className={`text-[10px] ${p.vigente_hoy
                      ? "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]"
                      : "bg-[#f7f9f8] text-[#5c7368] border border-[#d1dbd6]"}`}>
                      {p.vigente_hoy ? "Vigente" : "Histórico"}
                    </Badge>
                  </td>
                </tr>
              ))}
              {parametros.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-xs text-[#8ba397]">
                  Sin parámetros registrados. Cree por ejemplo "iva_general" o "retencion_bienes" con su vigencia.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fuentes vigiladas */}
      <div className="rounded-lg border border-[#d1dbd6] bg-white p-3" data-testid="cumpl-fuentes">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Fuentes vigiladas</div>
        <ul className="space-y-1 text-xs">
          {fuentes.map(f => (
            <li key={f.clave} className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {f.nombre} {f.version_base ? <span className="text-[#205c45]">· base v{f.version_base}</span> : null}
              </span>
              <span className="text-[11px] text-[#5c7368]">
                {f.ultimo_error
                  ? <span className="text-[#7a2514]">error: {f.ultimo_error}</span>
                  : `revisada ${(f.ultima_revision || "").slice(0, 16).replace("T", " ") || "nunca"}`}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {nuevaOpen && <NovedadDialog onClose={() => setNuevaOpen(false)} onSaved={() => { setNuevaOpen(false); load(); }} />}
      {paramOpen && <ParametroDialog onClose={() => setParamOpen(false)} onSaved={() => { setParamOpen(false); load(); }} />}
    </div>
  );
}

function NovedadDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ titulo: "", detalle: "", url: "", resolucion: "",
    prioridad: "importante", ambito: "software", fecha_limite: "" });
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    if (!form.titulo.trim()) return toast.error("Indique el título de la novedad");
    setSaving(true);
    try {
      await api.post("/cumplimiento/novedades", form);
      toast.success("Novedad registrada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="novedad-dialog">
        <DialogHeader><DialogTitle>Registrar novedad normativa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Título *</Label>
            <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="Resolución NAC-DGERCGC26-00000027 · proveedores de facturación" data-testid="novedad-titulo" /></div>
          <div><Label>Detalle</Label>
            <Textarea rows={3} value={form.detalle} onChange={(e) => setForm({ ...form, detalle: e.target.value })}
              data-testid="novedad-detalle" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Resolución</Label>
              <Input value={form.resolucion} onChange={(e) => setForm({ ...form, resolucion: e.target.value })}
                placeholder="NAC-DGERCGC26-00000027" data-testid="novedad-resolucion" /></div>
            <div><Label>Enlace oficial</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="novedad-url" /></div>
            <div>
              <Label>Prioridad</Label>
              <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
                <SelectTrigger data-testid="novedad-prioridad"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critico">Crítico · requiere cambio en el software</SelectItem>
                  <SelectItem value="importante">Importante · revisión contable</SelectItem>
                  <SelectItem value="empresarial">Empresarial · obligación de la empresa</SelectItem>
                  <SelectItem value="informativo">Informativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ámbito</Label>
              <Select value={form.ambito} onValueChange={(v) => setForm({ ...form, ambito: v })}>
                <SelectTrigger data-testid="novedad-ambito"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="software">Afecta al software</SelectItem>
                  <SelectItem value="empresa">Afecta a la empresa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Fecha límite</Label>
              <Input type="date" value={form.fecha_limite}
                onChange={(e) => setForm({ ...form, fecha_limite: e.target.value })} data-testid="novedad-limite" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-[#205c45]" data-testid="novedad-guardar">
            {saving ? "Guardando…" : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParametroDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ clave: "", descripcion: "", valor: "", tipo: "porcentaje",
    vigente_desde: new Date().toISOString().slice(0, 10), vigente_hasta: "", fuente: "", modulo: "" });
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    if (!form.clave.trim() || !form.valor.trim()) return toast.error("Clave y valor son obligatorios");
    setSaving(true);
    try {
      await api.post("/cumplimiento/parametros", { ...form, vigente_hasta: form.vigente_hasta || null });
      toast.success("Parámetro registrado · el anterior quedó en histórico");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="parametro-dialog">
        <DialogHeader><DialogTitle>Nuevo parámetro tributario</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Clave *</Label>
            <Input value={form.clave} onChange={(e) => setForm({ ...form, clave: e.target.value })}
              placeholder="iva_general / retencion_bienes / ficha_tecnica_sri" data-testid="param-clave" /></div>
          <div><Label>Valor *</Label>
            <Input value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })}
              placeholder="15 / 1.75 / 2.34" data-testid="param-valor" /></div>
          <div className="col-span-2"><Label>Descripción</Label>
            <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="IVA general para ventas" data-testid="param-descripcion" /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger data-testid="param-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="porcentaje">Porcentaje</SelectItem>
                <SelectItem value="valor">Valor</SelectItem>
                <SelectItem value="codigo">Código SRI</SelectItem>
                <SelectItem value="texto">Texto / versión</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Módulo</Label>
            <Input value={form.modulo} onChange={(e) => setForm({ ...form, modulo: e.target.value })}
              placeholder="facturacion / compras" data-testid="param-modulo" /></div>
          <div><Label>Vigente desde *</Label>
            <Input type="date" value={form.vigente_desde}
              onChange={(e) => setForm({ ...form, vigente_desde: e.target.value })} data-testid="param-desde" /></div>
          <div><Label>Vigente hasta</Label>
            <Input type="date" value={form.vigente_hasta}
              onChange={(e) => setForm({ ...form, vigente_hasta: e.target.value })} data-testid="param-hasta" /></div>
          <div className="col-span-2"><Label>Fuente / respaldo</Label>
            <Input value={form.fuente} onChange={(e) => setForm({ ...form, fuente: e.target.value })}
              placeholder="Resolución NAC-DGERCGC26-00000009" data-testid="param-fuente" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-[#205c45]" data-testid="param-guardar">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
